import React from 'react';
import {
  Badge,
  BedroomChild,
  BorderColor,
  CalendarMonth,
  CorporateFare,
  Description,
  DriveFileMove,
  FactCheck,
  FolderShared,
  MoveToInbox,
  Person,
  Rule,
  Settings,
  Task
} from '@mui/icons-material';
import ContactPageIcon from '@mui/icons-material/ContactPage';
import ScreenSearchDesktopIcon from '@mui/icons-material/ScreenSearchDesktop';

import {
  ScreenerSettingName,
  useIsScreenerSettingEnabled
} from '@/hooks/screener-settings';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useSelectedFacilityId } from '@/hooks/useFacilitiesQuery';
import { useIsMedicaidPendingAvailable } from '@/pages/CRM/MedicaidPending/hooks/useIsMedicaidPendingAvailable';
import { useCrmEsignAvailability } from '@/pages/CRM/Screener/hooks/useCrmEsignAvailability';
import { useIsBedBoardEnabled } from '@/pages/CRM/Screener/hooks/useIsBedBoardEnabled';
import { useIsInboundDocumentsEnabled } from '@/pages/CRM/Screener/hooks/useIsInboundDocumentsEnabled';
import { useIsReferralOutboundEnabled } from '@/pages/CRM/Screener/hooks/useIsReferralOutboundEnabled';
import { useIsScreenerLeadWaitlistEnabled } from '@/pages/CRM/Screener/hooks/useIsScreenerLeadWaitlistEnabled';
import { usePccNeedsReviewCount } from '@/pages/CRM/Screener/hooks/usePccNeedsReviewCount';
import useIsScreenerAllFacilitiesSelected from '@/pages/CRM/Screener/Leads/AllLeads/hooks/useIsScreenerAllFacilitiesSelected';
import { useDataCenterVersion } from '@/pages/DataCenter/v3/useDataCenterVersion';
import { RoutePath } from '@/routes/RoutePath';
import { ExacareFeature, FeatureFlagService } from '@/utils/featureFlagService';
import { generatePathWithDomain } from '@/utils/generatePathWithDomain';

export interface SidebarItem {
  icon: React.ReactNode;
  text: string;
  chameleonId?: string;
  path: string;
  activePaths?: string[];
  showOnMobile?: boolean;
  badgeCount?: number;
  onClick?: () => void;
}

export const useGetSidebarCRMItems = (): SidebarItem[] => {
  const selectedFacilityId = useSelectedFacilityId();
  const currentUser = useCurrentUser().data!;
  const canAccessAdminHub = currentUser.hasMinimumUserRole('L1');
  const isScreenerAllFacilitiesSelected = useIsScreenerAllFacilitiesSelected();
  const isBedBoardEnabled = useIsBedBoardEnabled();
  const isReferralOutboundEnabled = useIsReferralOutboundEnabled();
  const isCalendarEnabled = useIsScreenerSettingEnabled(
    ScreenerSettingName.ScreenerCalendar,
    selectedFacilityId ?? ''
  );
  const isWaitlistEnabled = useIsScreenerLeadWaitlistEnabled();
  const { isEnabled: isCrmEsignEnabled } = useCrmEsignAvailability();
  const inboundAvailability = useIsInboundDocumentsEnabled();
  const dataCenterVersion = useDataCenterVersion();
  const { needsReviewCount } = usePccNeedsReviewCount();
  const isMedicaidPendingAvailable = useIsMedicaidPendingAvailable();

  const preAuthSetting = useIsScreenerSettingEnabled(
    ScreenerSettingName.PalPreauth,
    selectedFacilityId as string
  );

  const isAuthorizationsEnabled =
    FeatureFlagService.isEnabled(
      ExacareFeature.CRM_SCREENER_PAL_PRE_AUTH_AUTHORIZATIONS_TABLE
    ) && preAuthSetting;

  return [
    {
      icon: <ScreenSearchDesktopIcon />,
      text: 'Screener',
      chameleonId: 'nav-screener',
      path: RoutePath.CRMScreenerAllLeads,
      showOnMobile: true
    },
    ...(inboundAvailability.status !== 'disabled'
      ? [
          {
            icon: <MoveToInbox />,
            text: 'Inbound',
            chameleonId: 'nav-inbound',
            path: RoutePath.CRMScreenerInbound,
            activePaths: [RoutePath.CRMScreenerInbound],
            showOnMobile: true
          }
        ]
      : []),
    ...(isAuthorizationsEnabled
      ? [
          {
            icon: <FactCheck />,
            text: 'Authorizations',
            chameleonId: 'nav-authorizations',
            path: RoutePath.CRMAuthorizations,
            showOnMobile: true
          }
        ]
      : []),
    ...(isWaitlistEnabled
      ? [
          {
            icon: <ContactPageIcon />,
            text: 'Waitlist',
            chameleonId: 'nav-waitlist',
            path: RoutePath.CRMWaitlist,
            showOnMobile: true
          }
        ]
      : []),
    ...(isBedBoardEnabled
      ? [
          {
            icon: <Person />,
            text: 'Residents',
            chameleonId: 'nav-residents',
            path: RoutePath.CrmScreenerResidentsAllResidents,
            showOnMobile: true,
            badgeCount: needsReviewCount
          }
        ]
      : []),
    ...(isBedBoardEnabled
      ? [
          {
            icon: <BedroomChild />,
            text: 'Bed Board',
            chameleonId: 'nav-bed-board',
            path: RoutePath.CRMBedboard,
            showOnMobile: true
          }
        ]
      : []),
    ...(isReferralOutboundEnabled
      ? [
          {
            icon: <DriveFileMove />,
            text: 'Outbound',
            chameleonId: 'nav-outbound',
            path: RoutePath.CRMReferralOutbound,
            showOnMobile: true
          }
        ]
      : []),
    ...(isReferralOutboundEnabled && isCalendarEnabled
      ? [
          {
            icon: <CalendarMonth />,
            text: 'Calendar',
            chameleonId: 'nav-calendar',
            path: RoutePath.CRMReferralOutboundCalendar,
            showOnMobile: true
          }
        ]
      : []),
    ...(FeatureFlagService.isEnabled(ExacareFeature.CRM_SCREENER_TASKS)
      ? [
          {
            icon: <Description />,
            text: 'Tasks',
            chameleonId: 'nav-tasks',
            path: RoutePath.CRMTasks,
            showOnMobile: true
          }
        ]
      : []),
    ...(isMedicaidPendingAvailable
      ? [
          {
            icon: <FolderShared />,
            text: 'Medicaid Pending',
            chameleonId: 'nav-medicaid-pending',
            path: RoutePath.CRMMedicaidPending
          }
        ]
      : []),
    ...(isCrmEsignEnabled
      ? [
          {
            icon: <BorderColor />,
            text: 'eSign',
            chameleonId: 'nav-esign',
            path: RoutePath.CRMScreenerESign,
            activePaths: [
              RoutePath.CRMScreenerESign,
              RoutePath.CRMScreenerESignNew,
              RoutePath.CRMScreenerSingleLeadESignNew,
              RoutePath.CRMScreenerSingleLeadESignRequest
            ],
            showOnMobile: true
          }
        ]
      : []),
    {
      icon: <Badge />,
      text: 'Sources',
      chameleonId: 'nav-sources',
      path: RoutePath.CRMSources,
      showOnMobile: true
    },
    ...(dataCenterVersion === 'v2'
      ? [
          {
            icon: <Task />,
            text: 'Data Center',
            chameleonId: 'nav-data-center',
            path: generatePathWithDomain(RoutePath.DataCenterDashboards)
          }
        ]
      : []),
    ...(canAccessAdminHub
      ? [
          {
            icon: <CorporateFare />,
            text: 'Admin Hub',
            chameleonId: 'nav-admin-hub',
            path: generatePathWithDomain(RoutePath.AdminHub)
          }
        ]
      : []),
    ...(!canAccessAdminHub &&
    !isScreenerAllFacilitiesSelected &&
    FeatureFlagService.isEnabled(ExacareFeature.SCREENER_ADMISSION_FLAGS)
      ? [
          {
            icon: <Settings />,
            text: 'Screener Settings',
            chameleonId: 'nav-screener-settings',
            path: RoutePath.AdminHubScreenerSettingsAdmissionAlerts
          }
        ]
      : []),
    ...(!currentUser.isAdmin() &&
    currentUser.isCRMScreenerManageAutoDecisioningEnabled()
      ? [
          {
            icon: <Rule />,
            text: 'Auto-decisioning',
            chameleonId: 'nav-auto-decisioning',
            path: RoutePath.AdminHubAutoDecisioningManage,
            activePaths: [RoutePath.AdminHubAutoDecisioning],
            showOnMobile: true
          }
        ]
      : [])
  ];
};
